import { Box, styled, Theme, Typography, useMediaQuery, useTheme } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { CandidateMatchArchiveCardInterface } from 'features/Connect/features/ConnectCandidatePortalDashboards/CandidateMatchDashboard/utils/types';
import { ConnectCandidateMatchArchiveCardDataTestIds } from 'data-testids/CandidatePortalDataTestIds';
import { formatDate, formatTime } from 'features/Connect/utils/connectUtils';

interface CandidateMatchArchiveCardTitleProps {
  archivedMatch: CandidateMatchArchiveCardInterface['archivedMatch'];
}

export const CandidateMatchArchiveCardTitle: React.FC<CandidateMatchArchiveCardTitleProps> = ({
  archivedMatch,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const actionDate = archivedMatch?.modified || archivedMatch?.created;

  const getActionTypeInfo = (status: string) => {
    switch (status) {
      case 'accepted':
        return {
          text: 'You responded: Interested',
          color: theme.palette.success.main,
          icon: CheckCircleIcon,
        };
      case 'rejected':
        return {
          text: 'You responded: No thanks',
          color: theme.palette.error.main,
          icon: CancelIcon,
        };
      case 'previously_actioned':
        return {
          text: 'You responded: Already connected',
          color: theme.palette.text.secondary,
          icon: null,
        };
      default:
        return {
          text: 'You responded',
          color: theme.palette.text.secondary,
          icon: null,
        };
    }
  };

  return (
    <ArchiveCardTitleContainer
      status={archivedMatch?.status || ''}
      data-testid={ConnectCandidateMatchArchiveCardDataTestIds.TITLE_CONTAINER}
    >
      <ArchiveCardTitleLeft>
        {(() => {
          const actionInfo = getActionTypeInfo(archivedMatch?.status);
          const IconComponent = actionInfo.icon;
          return (
            <ActionTypeContainer>
              {IconComponent && (
                <IconComponent
                  data-testid={ConnectCandidateMatchArchiveCardDataTestIds.TITLE_ICON}
                  sx={{
                    fontSize: theme.typography.body2.fontSize,
                    color: actionInfo.color,
                  }}
                />
              )}
              <ActionTypeText
                color={actionInfo.color}
                data-testid={ConnectCandidateMatchArchiveCardDataTestIds.TITLE_TEXT}
              >
                {actionInfo.text}
              </ActionTypeText>
            </ActionTypeContainer>
          );
        })()}
      </ArchiveCardTitleLeft>
      <ArchiveCardTitleRight>
        <ActionDateText data-testid={ConnectCandidateMatchArchiveCardDataTestIds.ACTION_DATE_TEXT}>
          {actionDate && (
            <>
              <span className="date">{formatDate(actionDate)}</span>
              {!isMobile && <span className="separator">, </span>}
              <span className="time">{formatTime(actionDate)}</span>
            </>
          )}
        </ActionDateText>
      </ArchiveCardTitleRight>
    </ArchiveCardTitleContainer>
  );
};

const ArchiveCardTitleContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'status',
})<{ status: string }>(({ theme, status }) => {
  const getBackgroundColor = (status: string) => {
    switch (status) {
      case 'accepted':
        return theme.palette.success.fill;
      case 'rejected':
        return theme.palette.error.fill;
      default:
        return theme.palette.white.medium;
    }
  };

  return {
    position: 'absolute',
    top: 0,
    left: 0,
    display: 'flex',
    width: '100%',
    height: '35px',
    padding: theme.spacing(2),
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '10px',
    flexShrink: 0,
    borderRadius: `${theme.shape.borderRadius * 2}px ${theme.shape.borderRadius * 2}px 0px 0px`,
    background: getBackgroundColor(status),

    [theme.breakpoints.down('md')]: {
      height: '25px',
      padding: theme.spacing(2.5, 1),
    },
  };
});

const ArchiveCardTitleLeft = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
}));

const ArchiveCardTitleRight = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
}));

const ActionTypeContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  whiteSpace: 'nowrap',
}));

const ActionTypeText = styled(Typography)(({ theme }) => ({
  color: 'inherit',
  fontSize: theme.typography.h3.fontSize,
  fontStyle: 'normal',
  fontWeight: theme.typography.fontWeightMedium,
  lineHeight: '19px',
  [theme.breakpoints.down('md')]: {
    fontSize: theme.typography.body2.fontSize,
  },
}));

const ActionDateText = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.light,
  fontSize: theme.typography.body2.fontSize,
  fontStyle: 'normal',
  fontWeight: theme.typography.fontWeightMedium,
  lineHeight: '19px',

  '& .date, & .time': {
    display: 'inline',
  },

  [theme.breakpoints.down('md')]: {
    fontSize: theme.typography.body3.fontSize,
    lineHeight: '16px',
    textAlign: 'right',
    whiteSpace: 'nowrap',

    '& .date, & .time': {
      display: 'block',
    },
  },
}));
